/** A view of the review already received from the updater, never a filesystem listing.
 * Unknown or ambiguous lines remain notes. Every input line retains its exact text
 * and index so callers can still offer the complete review in its original order.
 */
export interface UpdaterReviewEntry {
  action: string;
  line: string;
  index: number;
  detail?: string;
  change?: 'new' | 'changed' | 'no-longer';
  /** Only true when the received format explicitly identifies a directory. */
  directory?: boolean;
}

export interface UpdaterReviewFile {
  path: string;
  entries: UpdaterReviewEntry[];
  directory?: boolean;
}

export type UpdaterReviewSection = 'summary' | 'source' | 'amp' | 'compatibility' | 'preserved' | 'general';

export interface UpdaterReviewNote {
  line: string;
  index: number;
  section: UpdaterReviewSection;
}

export interface UpdaterChangeReview {
  /** Files and their entries keep first-appearance order; repeated paths are not discarded. */
  files: UpdaterReviewFile[];
  notes: UpdaterReviewNote[];
  /** Counts of parsed review records, not a claim about the full plan or pending mutations. */
  counts: Record<string, number>;
}

interface ParsedEntry {
  path: string;
  action: string;
  detail?: string;
  change?: UpdaterReviewEntry['change'];
  directory?: boolean;
}

function relativePath(value: string, directory = false): { path: string; directory?: boolean } | null {
  // Linux pack paths are relative. Do not turn URLs, AMP keys, source locations,
  // provider project IDs, or traversal-looking text into virtual file entries.
  if (!value || value !== value.trim() || /[\x00-\x1f\x7f\\:]/.test(value)
    || value.startsWith('/') || /^project \d+$/i.test(value)) return null;
  const trailingSlash = value.endsWith('/');
  const path = trailingSlash ? value.slice(0, -1) : value;
  if (path.split('/').some(part => !part || part === '.' || part === '..')) return null;
  // An unqualified sentence is not a root filename. Root names with spaces are
  // still accepted when a filename extension or an explicit directory proves intent.
  if (!directory && !trailingSlash && !path.includes('/') && /\s/.test(path) && !/\.[^\s.]+$/.test(path)) return null;
  return { path, ...(directory || trailingSlash ? { directory: true } : {}) };
}

function entry(action: string, path: string, rest: Omit<ParsedEntry, 'path' | 'action'> = {}): ParsedEntry | null {
  const parsed = relativePath(path, rest.directory);
  return parsed ? { action, ...rest, ...parsed } : null;
}

/** The emitted annotation uses TWO spaces; parentheses inside filenames stay intact. */
function annotated(value: string): { path: string; detail?: string } {
  const match = /^(.*) {2}\((.*)\)$/.exec(value);
  return match ? { path: match[1], detail: match[2] } : { path: value };
}

function deltaAnnotation(value: string, kind: 'changed' | 'baseline' | 'directory') {
  // Delta annotations contain their own parentheses (byte counts), so splitting
  // at the last parenthesis would corrupt both paths and details.
  const starts = kind === 'changed'
    ? '(?:action (?:add|replace|remove) -> (?:add|replace|remove)|live file:|target file:|replacement source changed)'
    : kind === 'baseline' ? '(?:now present|now absent|contents changed|permissions)'
      : '(?:created after shutdown|removed after shutdown|permissions)';
  const match = new RegExp(`^(.*?) \\((${starts}.*)\\)$`).exec(value);
  if (!match) return null;
  if (kind === 'baseline' && !match[2].endsWith('; no changed update operation')) return null;
  return { path: match[1], detail: match[2] };
}

function parseEntry(line: string): ParsedEntry | null {
  let match = /^(NEW|CHANGED|NO LONGER) (ADD|REPLACE|REMOVE) {2}(.+)$/.exec(line);
  if (match) {
    const change = { NEW: 'new', CHANGED: 'changed', 'NO LONGER': 'no-longer' }[match[1]] as UpdaterReviewEntry['change'];
    let value: { path: string; detail?: string } | null = { path: match[3] };
    if (change === 'changed') value = deltaAnnotation(match[3], 'changed');
    if (change === 'no-longer') {
      const suffix = ' (no update operation)';
      value = match[3].endsWith(suffix) ? { path: match[3].slice(0, -suffix.length), detail: 'no update operation' } : null;
    }
    return value ? entry(match[2].toLowerCase(), value.path, { change, detail: value.detail }) : null;
  }

  match = /^(ROLLBACK BASELINE|ROLLBACK DIRECTORY) {2}(.+)$/.exec(line);
  if (match) {
    const directory = match[1] === 'ROLLBACK DIRECTORY';
    const value = deltaAnnotation(match[2], directory ? 'directory' : 'baseline');
    return value ? entry(directory ? 'rollback-directory' : 'rollback-baseline', value.path,
      { detail: value.detail, ...(directory ? { directory: true } : {}) }) : null;
  }

  match = /^(MOD DECISION|DIRECTORY|EXCLUDE DIRECTORY) {2}(.+) · (.+)$/.exec(line);
  if (match) {
    const action = { 'MOD DECISION': 'mod-decision', DIRECTORY: 'directory', 'EXCLUDE DIRECTORY': 'exclude-directory' }[match[1]]!;
    if (action === 'mod-decision' && !['keep', 'exclude'].includes(match[3])) return null;
    return entry(action, match[2], { detail: match[3], ...(action !== 'mod-decision' ? { directory: true } : {}) });
  }

  // Historical reviews and template/rollback summaries use the same action +
  // relative-path shape. Optional annotations never invent file contents.
  match = /^(BACKUP CHANGED|ADD|REPLACE|REMOVE|BACKUP|KEEP|IGNORE|APPLY|RESTORE|EXCLUDE|REVIEW|DISABLE) +(.+)$/.exec(line);
  if (!match) return null;
  // Mutation lines from plan_lines have no suffix; a parenthesized filename
  // must not become a fabricated description. Only annotated record types
  // are split; any punctuation in ordinary mutation paths stays literal.
  let value: { path: string; detail?: string } = ['BACKUP CHANGED', 'BACKUP', 'KEEP', 'IGNORE', 'EXCLUDE', 'REVIEW'].includes(match[1])
    ? annotated(match[2]) : { path: match[2] };
  if (match[1] === 'BACKUP CHANGED') {
    const changed = deltaAnnotation(match[2], 'changed') || deltaAnnotation(match[2], 'baseline');
    if (changed) value = changed;
  }
  return entry(match[1] === 'BACKUP CHANGED' ? 'backup' : match[1].toLowerCase(), value.path,
    { detail: value.detail, ...(match[1] === 'BACKUP CHANGED' ? { change: 'changed' as const } : {}) });
}

function noteSection(line: string): UpdaterReviewSection {
  if (/^AMP(?:\s|:)|^Enabled AMP triggers/.test(line)) return 'amp';
  if (/^(?:Source:|Provider:|Pack:|Release:|Candidate:|EXCLUDE(?: DIRECTORY)?\s|REVIEW\s|MOD DECISION\s|DIRECTORY\s)/.test(line)) return 'source';
  if (/^(?:COMPATIBILITY\s|MINECRAFT TRANSITION\s|Candidate Minecraft:|The installed launch artifacts|Check the pack's migration|Starting the candidate can migrate)/.test(line)) return 'compatibility';
  if (/^(?:Preserved files and directories|Every world folder|World folders|Templates targeting a world|Custom mods|KEEP\s)/.test(line)) return 'preserved';
  if (/^(?:Add:|Managed-directory backup:|Managed backup:|Files to restore\/remove:|Recorded installation:|Server:|Instance:|Template tree:|Rollback transaction:|Transaction:|Game:)/.test(line)) return 'summary';
  return 'general';
}

export function parseUpdaterChangeReview(lines: readonly string[]): UpdaterChangeReview {
  const files = new Map<string, UpdaterReviewFile>();
  const notes: UpdaterReviewNote[] = [];
  const counts: Record<string, number> = {};
  lines.forEach((line, index) => {
    const parsed = parseEntry(line);
    if (!parsed) {
      notes.push({ line, index, section: noteSection(line) });
      return;
    }
    const { path, ...rest } = parsed;
    let file = files.get(path);
    if (!file) {
      file = { path, entries: [] };
      files.set(path, file);
    }
    if (parsed.directory) file.directory = true;
    file.entries.push({ ...rest, line, index });
    counts[parsed.action] = (counts[parsed.action] ?? 0) + 1;
  });
  return { files: [...files.values()], notes, counts };
}
